/* @flow @format */

import type { Reducer } from 'redux';
import { createSelector } from 'reselect';

import { type ActionType, createAction } from '../utils';
import type { CustomSelector, FailurePayload } from '../types';
import type { RefreshAuth_refreshAuth } from '../../graphql/generated_types';
import type { TicketSigninInput } from '../../graphql/api';
import AWS from '../../../aws';

export type S3Credential = {
  +accessKeyId: string,
  +expiration: string,
  +region: string,
  +resource: string,
  +secretAccessKey: string,
  +sessionToken: string,
};

export type Auth = {
  +accessToken: string,
  +resourceToken: string,
  +refreshToken: string,
  +sts: S3Credential,
};

// Actions

const SIGNIN_BEGIN: 'proc/auth/SIGNIN_BEGIN' = 'proc/auth/SIGNIN_BEGIN';
const SIGNIN_SUCCESS: 'proc/auth/SIGNIN_SUCCESS' = 'proc/auth/SIGNIN_SUCCESS';
const SIGNIN_FAILURE: 'proc/auth/SIGNIN_FAILURE' = 'proc/auth/SIGNIN_FAILURE';
const TOKEN_REFRESHED: 'proc/auth/TOKEN_REFRESHED' = 'proc/auth/TOKEN_REFRESHED';

type SigninFailurePayload = FailurePayload<TicketSigninInput>;

export const signinBegin = createAction<typeof SIGNIN_BEGIN, TicketSigninInput>(SIGNIN_BEGIN);
export const signinSuccess = createAction<typeof SIGNIN_SUCCESS, Auth>(SIGNIN_SUCCESS);
export const signinFailure = createAction<typeof SIGNIN_FAILURE, SigninFailurePayload>(SIGNIN_FAILURE);
export const tokenRefreshed = createAction<typeof TOKEN_REFRESHED, RefreshAuth_refreshAuth>(TOKEN_REFRESHED);

export type AuthActionType =
  | ActionType<typeof signinBegin>
  | ActionType<typeof signinSuccess>
  | ActionType<typeof signinFailure>
  | ActionType<typeof tokenRefreshed>;

// Reducer

const reducer: Reducer<?Auth, AuthActionType> = (state = null, action) => {
  switch (action.type) {
    case SIGNIN_SUCCESS:
    case TOKEN_REFRESHED:
      return action.payload;
    case SIGNIN_BEGIN:
      return null;
    case SIGNIN_FAILURE:
      return state;
    default:
      /*::(action.type: empty)*/
      return state;
  }
};

export default reducer;

// Selectors

type AuthSelector<T> = CustomSelector<?Auth, T>;

export const getAccessToken: AuthSelector<?string> = state => state && state.accessToken;
export const getResourceToken: AuthSelector<?string> = state => state && state.resourceToken;
export const getSts: AuthSelector<?S3Credential> = state => state && state.sts;

export const getUserS3: AuthSelector<any> = createSelector(
  getSts,
  sts => sts && new AWS.S3({ ...sts, correctClockSkew: true })
);

export const getS3BucketAndPath: AuthSelector<?[string, string]> = createSelector(getSts, sts => {
  if (!sts) return null;

  const matches = sts.resource.match(/rn:aws:s3:::(.+?)\/(.+\/)\*/);
  if (!matches) return null;

  const [, bucket, path] = matches;
  return [bucket, path];
});
